/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import { buildApiUrl } from '../config/api';
import '@fortawesome/fontawesome-free/css/all.css';
import QuickActions from '../components/QuickActions';
import '../styles/RecruiterDashboard.css';
import RecruiterCandidateTracker from '../components/RecruiterCandidateTracker';
import InterviewCalendar from '../components/InterviewCalendar';
import UpcomingInterviewsPanel from '../components/UpcomingInterviewsPanel';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import RecruiterSettings from '../components/RecruiterSettings';
import StatCard from '../components/StatCard';
import AksharCoinsWidget from '../components/AksharCoinsWidget';
import ProfessionalMessaging from '../components/ProfessionalMessaging';
import EmployerRecruiterHeader from '../components/EmployerRecruiterHeader';
import { faBriefcase, faUsers, faUserClock, faHandshake, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const EMPLOYER_ACTION_MENU_VIEWPORT_PADDING = 10;
const EMPLOYER_ACTION_MENU_WIDTH = 160;

const computeFixedMenuPosition = (triggerRect) => {
  const padding = EMPLOYER_ACTION_MENU_VIEWPORT_PADDING;
  const viewportWidth = window.innerWidth;
  const menuWidth = Math.min(EMPLOYER_ACTION_MENU_WIDTH, viewportWidth - padding * 2);

  let left = triggerRect.left;
  if (left + menuWidth > viewportWidth - padding) {
    left = triggerRect.right - menuWidth;
  }
  left = Math.max(padding, Math.min(left, viewportWidth - menuWidth - padding));

  const estimatedMenuHeight = 140;
  let top = triggerRect.bottom + 6;
  if (top + estimatedMenuHeight > window.innerHeight - padding) {
    top = Math.max(padding, triggerRect.top - estimatedMenuHeight - 6);
  }

  return { left, top, width: menuWidth };
};

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id: viewUserId } = useParams(); // Get userId from URL if admin is viewing another user's dashboard
  const isAdminView = !!viewUserId && (user?.role === 'admin' || user?.userType === 'admin');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [jobs, setJobs] = useState([]);
  const [internships, setInternships] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [internshipApplications, setInternshipApplications] = useState([]);
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    inInterview: 0,
    offersExtended: 0,
    newJobsThisWeek: 0,
    applicationsIncrease: 0,
    interviewsThisWeek: 0,
    offersAccepted: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFilter, setJobFilter] = useState('all'); // all, active, draft, closed
  const [internshipFilter, setInternshipFilter] = useState('all');
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [openJobMenu, setOpenJobMenu] = useState(null);
  const [activeJobForMenu, setActiveJobForMenu] = useState(null);
  const [jobMenuPosition, setJobMenuPosition] = useState(null);
  const [openInternshipMenu, setOpenInternshipMenu] = useState(null);
  const [activeInternshipForMenu, setActiveInternshipForMenu] = useState(null);
  const [internshipMenuPosition, setInternshipMenuPosition] = useState(null);
  
  // Job view/edit modal states
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editJobData, setEditJobData] = useState({});

  // Profile completion for sidebar (same as employer dashboard)
  const [profileCompletion, setProfileCompletion] = useState(0);

  // Settings update handler
  const handleUserUpdate = (updatedUser) => {
    // This would typically update the user context
    console.log('User updated:', updatedUser);
  };

  const handlePostJobClick = () => {
    navigate('/post-job', { state: { from: '/recruiter-dashboard' } });
  };
  const handlePostInternshipClick = () => {
    navigate('/post-internship', { state: { from: '/recruiter-dashboard' } });
  };

  const fetchProfileCompletion = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return undefined;
      const response = await fetch(buildApiUrl('/api/user/profile'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return undefined;
      const profileData = await response.json();
      const completion = profileData?.profileCompletion ?? profileData?.profileStatus?.completionPercentage ?? (profileData?.profileCompleted ? 100 : 0);
      const value = Math.max(0, Math.min(100, Number(completion) || 0));
      setProfileCompletion(value);
      return value;
    } catch (error) {
      console.error('Error fetching profile completion:', error);
      return undefined;
    }
  }, []);

  // Date formatting function - same as candidate tracker
  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
      if (value.$date) return value.$date;
      if (value.seconds) return new Date(value.seconds * 1000).toISOString();
      if (value._seconds) return new Date(value._seconds * 1000).toISOString();
    }
    try {
      const direct = new Date(value);
      if (!Number.isNaN(direct.getTime())) {
        return direct.toISOString();
      }
    } catch (err) {
      console.warn('Unable to normalize date value', value, err);
    }
    return null;
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    
    // Handle already formatted dates (MM/DD/YYYY or M/D/YYYY)
    if (typeof dateInput === 'string') {
      // Check for MM/DD/YYYY or M/D/YYYY format
      const dateMatch = dateInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dateMatch) {
        const [, month, day, year] = dateMatch;
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!Number.isNaN(date.getTime())) {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
        }
      }
    }
    
    const normalized = normalizeDateValue(dateInput);
    if (!normalized) return 'N/A';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return 'N/A';
    // Format as "Jan 21, 2026" to match candidate tracker - explicit formatting
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  useEffect(() => {
    fetchProfileCompletion();
    fetchAllData();
    const interval = setInterval(() => {
      fetchApplications();
    }, 30000);
    return () => clearInterval(interval);
  }, [viewUserId, isAdminView, fetchProfileCompletion]); // Reload when viewUserId changes (for admin view mode)

  const handleViewCandidate = (candidateId, jobId, applicationType = 'job') => {
    if (!candidateId) {
      return;
    }

    const normalizedType = applicationType === 'internship' ? 'internship' : 'job';
    const targetPath =
      normalizedType === 'internship'
        ? `/intern-profile/${candidateId}`
        : `/profile/${candidateId}`;

    navigate(targetPath, { state: { from: '/recruiter-dashboard', jobId } });
  };

  useEffect(() => {
    if (jobs.length > 0 || applications.length > 0 || internships.length > 0 || internshipApplications.length > 0) {
      const timeoutId = setTimeout(() => {
        fetchStats();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [jobs, applications, internships, internshipApplications]);

  useEffect(() => {
    const closeMenus = () => {
      setOpenInternshipMenu(null);
      setActiveInternshipForMenu(null);
      setInternshipMenuPosition(null);
      setOpenJobMenu(null);
      setActiveJobForMenu(null);
      setJobMenuPosition(null);
    };
    document.addEventListener('click', closeMenus);
    window.addEventListener('scroll', closeMenus, true);
    window.addEventListener('resize', closeMenus);
    return () => {
      document.removeEventListener('click', closeMenus);
      window.removeEventListener('scroll', closeMenus, true);
      window.removeEventListener('resize', closeMenus);
    };
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchJobs(),
        fetchInternships(),
        fetchCandidates(),
        fetchApplications()
      ]);
      fetchStats();
      
      // DISABLED: Daily login bonus feature
      // try {
      //   console.log('🔍 Checking daily login bonus...');
      //   const token = localStorage.getItem('token');
      //   const headers = { Authorization: `Bearer ${token}` };
      //   const dailyBonusRes = await fetch(buildApiUrl('/api/coins/daily-login'), {
      //     method: 'POST',
      //     headers: {
      //       ...headers,
      //       'Content-Type': 'application/json'
      //     }
      //   });
      //   
      //   if (dailyBonusRes.ok) {
      //     const data = await dailyBonusRes.json();
      //     console.log('🔍 Daily bonus response:', data);
      //     
      //     if (data.coins_awarded) {
      //       console.log(`🪙 Daily login bonus awarded: ${data.coins_amount} coins`);
      //       // Show alert to user
      //       alert(`🎉 Daily Login Bonus!\n\nYou earned ${data.coins_amount} Akshar Coins for logging in today!`);
      //       
      //       // Also try to show notification if available
      //       if (window.notificationApi && window.notificationApi.create) {
      //         await window.notificationApi.create({
      //           title: 'Daily Login Bonus!',
      //           message: `You earned ${data.coins_amount} Akshar Coins for logging in today! 🎉`,
      //           type: 'coin_award'
      //         });
      //       }
      //     } else if (data.already_awarded) {
      //       console.log('ℹ️ Daily bonus already claimed today');
      //     }
      //   } else {
      //     const errorData = await dailyBonusRes.json();
      //     console.error('❌ Daily bonus failed:', errorData);
      //   }
      // } catch (dailyBonusError) {
      //   console.error('❌ Daily bonus check failed:', dailyBonusError);
      //   // Don't fail dashboard load if daily bonus fails
      // }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        return;
      }
      
      // Fetch only jobs posted by this recruiter (not other employers' jobs)
      const url = isAdminView && viewUserId 
        ? buildApiUrl(`/api/jobs/recruiter?userId=${viewUserId}`)
        : buildApiUrl('/api/jobs/recruiter');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const jobsArray = Array.isArray(data) ? data : [];
        const transformedJobs = jobsArray.map(job => ({
          ...job,
          jobTitle: job.job_title || job.jobTitle || '',
          companyName: job.company_name || job.companyName || '',
          salary: job.salary_range || job.salary || '',
          salaryRange: job.salary_range || job.salaryRange || '',
          remoteOption: job.remote_option || job.remoteOption || '',
          jobType: job.job_type || job.jobType || '',
          experienceRequired: job.experience_required || job.experienceRequired || '',
          requiredSkills: job.required_skills || job.requiredSkills || [],
          educationRequired: job.education_required || job.educationRequired || '',
          applicationDeadline: job.application_deadline || job.applicationDeadline || '',
          companyWebsite: job.company_website || job.companyWebsite || ''
        }));
        
        // Sort jobs by created_at date (latest first)
        const sortedJobs = transformedJobs.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || a.postedDate || 0);
          const dateB = new Date(b.created_at || b.createdAt || b.postedDate || 0);
          return dateB - dateA; // Descending order (latest first)
        });
        
        setJobs(sortedJobs);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch jobs:', response.status, errorData);
      }
    } catch (error) {
      console.error('❌ Error fetching jobs:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
    }
  };

  const fetchInternships = async () => {
    try {
      const token = localStorage.getItem('token');
      // If admin is viewing another user's dashboard, include userId parameter
      const url = isAdminView && viewUserId 
        ? buildApiUrl(`/api/recruiters/internships?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/internships');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const transformedInternships = (data || []).map(job => ({
          ...job,
          jobTitle: job.job_title || job.jobTitle,
          companyName: job.company_name || job.companyName,
          salary: job.salary_range || job.salary,
          salaryRange: job.salary_range || job.salaryRange,
          remoteOption: job.remote_option || job.remoteOption,
          jobType: job.job_type || job.jobType,
          experienceRequired: job.experience_required || job.experienceRequired,
          requiredSkills: job.required_skills || job.requiredSkills,
          educationRequired: job.education_required || job.educationRequired,
          applicationDeadline: job.application_deadline || job.applicationDeadline,
          companyWebsite: job.company_website || job.companyWebsite
        }));
        
        // Sort internships by created_at/postedAt date (latest first)
        const sortedInternships = transformedInternships.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || a.postedAt || a.postedDate || 0);
          const dateB = new Date(b.created_at || b.createdAt || b.postedAt || b.postedDate || 0);
          return dateB - dateA; // Descending order (latest first)
        });
        
        setInternships(sortedInternships);
      }
    } catch (error) {
      console.error('Error fetching internships:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      // If admin is viewing another user's dashboard, include userId parameter
      const url = isAdminView && viewUserId 
        ? buildApiUrl(`/api/recruiters/candidates?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/candidates');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        return;
      }
      
      // Fetch job applications - include userId if admin view
      const applicationsUrl = isAdminView && viewUserId 
        ? buildApiUrl(`/api/recruiters/applications?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/applications');
      const response = await fetch(applicationsUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const applicationsArray = Array.isArray(data) ? data : [];
        setApplications(applicationsArray);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch applications:', response.status, errorData);
        setApplications([]);
      }
      
      // Fetch internship applications
      try {
        // Fetch internship applications - include userId if admin view
        const internApplicationsUrl = isAdminView && viewUserId 
          ? buildApiUrl(`/api/recruiters/internship-applications?userId=${viewUserId}`)
          : buildApiUrl('/api/recruiters/internship-applications');
        const internResponse = await fetch(internApplicationsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (internResponse.ok) {
          const internData = await internResponse.json();
          const internAppsArray = Array.isArray(internData) ? internData : [];
          setInternshipApplications(internAppsArray);
        } else {
          setInternshipApplications([]);
        }
      } catch (internError) {
        console.error('Error fetching internship applications:', internError);
        setInternshipApplications([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      setApplications([]);
    }
  };

  const fetchStats = async () => {
    try {
      const activeJobs = jobs.length;
      // Combine job applications and internship applications for total count
      const totalApplications = applications.length + internshipApplications.length;
      const inInterview = [...applications, ...internshipApplications].filter(app => app.status === 'interview').length;
      const offersExtended = [...applications, ...internshipApplications].filter(app => app.status === 'offered').length;
      
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const newJobsThisWeek = jobs.filter(job => {
        const jobDate = new Date(job.created_at || job.createdAt || job.postedDate);
        return jobDate >= oneWeekAgo;
      }).length;
      
      const totalJobApplications = jobs.reduce((total, job) => {
        return total + (job.applicationCount || job.applicants?.length || 0);
      }, 0);
      
      const calculatedStats = {
        activeJobs: activeJobs,
        totalApplications: Math.max(totalApplications, totalJobApplications),
        inInterview: inInterview,
        offersExtended: offersExtended,
        newJobsThisWeek: newJobsThisWeek,
        applicationsIncrease: 0,
        interviewsThisWeek: 0,
        offersAccepted: 0
      };
      
      setStats(calculatedStats);
    } catch (error) {
      console.error('Error calculating stats:', error);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setIsEditing(false);
    setShowJobModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setEditJobData({
      job_title: job.job_title || job.title || '',
      company_name: job.company_name || job.company || '',
      location: job.location || '',
      job_type: job.job_type || job.type || '',
      experience_required: job.experience_required || '',
      required_skills: job.required_skills || '',
      industry: job.industry || '',
      remote_option: job.remote_option || '',
      description: job.description || '',
      responsibilities: job.responsibilities || '',
      salary_range: job.salary_range || '',
      application_deadline: job.application_deadline || '',
      education_required: job.education_required || '',
      showCompanyName: job.showCompanyName !== false && job.show_company_name !== false,
      showHrName: job.showHrName !== false && job.show_hr_name !== false
    });
    setIsEditing(true);
    setShowJobModal(true);
  };

  const handleSaveJob = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine if it's a job or internship based on the selectedJob data
      const isInternship = selectedJob.duration || selectedJob.stipend || 
                           (selectedJob.jobTitle && !selectedJob.job_title && selectedJob.companyName);
      
      let endpoint, dataToSend;
      
      if (isInternship) {
        // Update internship
        endpoint = `/api/recruiters/internships/${selectedJob._id}`;
        dataToSend = editJobData;
      } else {
        // Update job
        endpoint = `/api/jobs/update_job/${selectedJob._id}`;
        dataToSend = editJobData;
      }
      
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        // Refresh both jobs and internships to ensure data is up-to-date
        if (isInternship) {
          await fetchInternships();
        } else {
          await fetchJobs();
        }
        setShowJobModal(false);
        setSelectedJob(null);
        setEditJobData({});
        alert(isInternship ? 'Internship updated successfully!' : 'Job updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to update ${isInternship ? 'internship' : 'job'}. ${errorData.error || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error updating:', error);
      alert('Error updating. Please try again.');
    }
  };

  const computeMenuPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return computeFixedMenuPosition(rect);
  };

  const handleToggleJobMenu = (menuId, job, event) => {
    event.stopPropagation();
    if (openJobMenu === menuId) {
      setOpenJobMenu(null);
      setActiveJobForMenu(null);
      setJobMenuPosition(null);
    } else {
      setOpenJobMenu(menuId);
      setActiveJobForMenu(job);
      setJobMenuPosition(computeMenuPosition(event));
    }
  };

  const handleJobMenuAction = async (action) => {
    if (!activeJobForMenu) return;
    if (action === 'view') {
      handleViewJob(activeJobForMenu);
    } else if (action === 'edit') {
      handleEditJob(activeJobForMenu);
    } else if (action === 'delete') {
      if (!window.confirm(`Are you sure you want to delete "${activeJobForMenu.job_title || activeJobForMenu.title || 'this job'}"? This cannot be undone.`)) {
        setOpenJobMenu(null);
        setActiveJobForMenu(null);
        setJobMenuPosition(null);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(buildApiUrl(`/api/jobs/jobs/${activeJobForMenu._id}`), {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          await fetchJobs();
          alert('Job deleted successfully.');
        } else {
          const err = await response.json().catch(() => ({}));
          alert(err.error || 'Failed to delete job.');
        }
      } catch (e) {
        console.error('Error deleting job:', e);
        alert('Error deleting job. Please try again.');
      }
    }
    setOpenJobMenu(null);
    setActiveJobForMenu(null);
    setJobMenuPosition(null);
  };

  const handleViewInternship = (internship) => {
    setSelectedJob(internship);
    setIsEditing(false);
    setShowJobModal(true);
  };

  const handleEditInternship = (internship) => {
    setSelectedJob(internship);
    setEditJobData({
      job_title: internship.jobTitle || internship.title || '',
      company_name: internship.companyName || internship.company || '',
      location: internship.location || '',
      duration: internship.duration || '',
      stipend: internship.stipend || '',
      description: internship.description || '',
      showCompanyName: internship.showCompanyName !== false && internship.show_company_name !== false,
      showHrName: internship.showHrName !== false && internship.show_hr_name !== false
    });
    setIsEditing(true);
    setShowJobModal(true);
  };

  const handleToggleInternshipMenu = (menuId, internship, event) => {
    event.stopPropagation();
    if (openInternshipMenu === menuId) {
      setOpenInternshipMenu(null);
      setActiveInternshipForMenu(null);
      setInternshipMenuPosition(null);
    } else {
      setOpenInternshipMenu(menuId);
      setActiveInternshipForMenu(internship);
      setInternshipMenuPosition(computeMenuPosition(event));
    }
  };

  const handleInternshipMenuAction = async (action) => {
    if (!activeInternshipForMenu) return;
    if (action === 'view') {
      handleViewInternship(activeInternshipForMenu);
    } else if (action === 'edit') {
      handleEditInternship(activeInternshipForMenu);
    } else if (action === 'delete') {
      const title = activeInternshipForMenu.jobTitle || activeInternshipForMenu.job_title || activeInternshipForMenu.title || 'this internship';
      if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
        setOpenInternshipMenu(null);
        setActiveInternshipForMenu(null);
        setInternshipMenuPosition(null);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(buildApiUrl(`/api/recruiters/internships/${activeInternshipForMenu._id}`), {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          await fetchInternships();
          alert('Internship deleted successfully.');
        } else {
          const err = await response.json().catch(() => ({}));
          alert(err.error || 'Failed to delete internship.');
        }
      } catch (e) {
        console.error('Error deleting internship:', e);
        alert('Error deleting internship. Please try again.');
      }
    }
    setOpenInternshipMenu(null);
    setActiveInternshipForMenu(null);
    setInternshipMenuPosition(null);
  };

  const handleCloseModal = () => {
    setShowJobModal(false);
    setSelectedJob(null);
    setIsEditing(false);
    setEditJobData({});
  };

  if (loading) {
    return (
      <ThemedLoadingSpinner 
        theme="recruiter"
        size="large"
        text="Loading your dashboard..."
        subText="Preparing your recruitment tools"
        showIcon={true}
        fullScreen={true}
      />
    );
  }

  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'jobs': 'Post Job',
      'internships': 'Post Internship',
      'post-job': 'Post Job',
      'my-jobs': 'My Jobs',
      'candidate-tracker': 'Candidate Tracker',
      'messages': 'Messages',
      'calendar': 'Interview Calendar',
      'analytics': 'Analytics',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  const actionButtonStyle = {
    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
    border: 'none',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '5px',
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    minWidth: 'auto',
    transition: 'all 0.2s ease',
    fontWeight: '500'
  };

  const searchContainerStyle = {
    position: 'relative',
    width: '360px',
    maxWidth: '100%',
    marginLeft: '-24px'
  };

  return (
    <>
      {/* Dropdown Override Styles */}
      <style>{`
        .filter-dropdown-compact {
          width: auto !important;
          max-width: 180px !important;
          min-width: unset !important;
        }
      `}</style>
      {/* Modern Header */}
      {isAdminView && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '16px 24px',
          margin: '20px auto',
          maxWidth: '1400px',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faUser} style={{ fontSize: '20px' }} />
            <div>
              <strong>Admin View Mode</strong>
              <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                Viewing dashboard for user: {viewUserId}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin?view=profile-browser')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Back to Profile Browser
          </button>
        </div>
      )}
      <EmployerRecruiterHeader userType="recruiter" />

      <div className="dashboard-container recruiter-dashboard" style={{ marginTop: '0' }}>
        {/* Sidebar */}
        <div className="sidebar" id="sidebar" style={{
          background: 'linear-gradient(180deg, #ff6b35 0%, #10b981 50%, #14b8a6 100%)',
          backgroundColor: '#ff6b35',
          backgroundImage: 'linear-gradient(180deg, #ff6b35 0%, #10b981 50%, #14b8a6 100%)',
          color: '#ffffff',
          position: 'fixed',
          left: '0',
          top: '80px',
          width: '320px',
          height: 'calc(100vh - 80px)',
          zIndex: 1000,
          paddingTop: '20px'
        }}>
        <div className="nav-menu">
          <div className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`} 
               onClick={() => setActiveSection('dashboard')}>
            <i className="fas fa-th-large"></i>
            <span>Dashboard</span>
          </div>
          <div className={`nav-item ${activeSection === 'jobs' ? 'active' : ''}`} 
               onClick={() => setActiveSection('jobs')}>
            <i className="fas fa-briefcase"></i>
            <span>Post Job</span>
            {jobs.length > 0 && <span className="badge">{jobs.length}</span>}
          </div>
          <div className={`nav-item ${activeSection === 'internships' ? 'active' : ''}`} onClick={() => setActiveSection('internships')}>
            <i className="fas fa-user-graduate"></i>
            <span>Post Internship</span>
            {internships.length > 0 && <span className="badge">{internships.length}</span>}
          </div>
          <div className={`nav-item ${activeSection === 'candidate-tracker' ? 'active' : ''}`} onClick={() => setActiveSection('candidate-tracker')}>
            <i className="fas fa-chart-line"></i>
            <span>Candidate Tracker</span>
            {applications.length > 0 && <span className="badge">{applications.length}</span>}
          </div>
          {/* TEMPORARILY DISABLED - Messages */}
          {/* <div className={`nav-item ${activeSection === 'messages' ? 'active' : ''}`} onClick={() => setActiveSection('messages')}>
            <i className="fas fa-envelope"></i>
            <span>Messages</span>
            {applications.filter(app => app.status === 'pending').length > 0 && 
              <span className="badge">{applications.filter(app => app.status === 'pending').length}</span>}
          </div> */}
          <div className={`nav-item ${activeSection === 'calendar' ? 'active' : ''}`} onClick={() => setActiveSection('calendar')}>
            <i className="fas fa-calendar-alt"></i>
            <span>Interview Calendar</span>
          </div>
          {/* TEMPORARILY DISABLED - Analytics */}
          {/* <div className={`nav-item ${activeSection === 'analytics' ? 'active' : ''}`} onClick={() => setActiveSection('analytics')}>
            <i className="fas fa-chart-bar"></i>
            <span>Analytics</span>
          </div> */}
          {/* TEMPORARILY DISABLED - Akshar Coins */}
          {/* <div className="nav-item" onClick={() => navigate('/promo-code#aksharCoins')}>
            <i className="fas fa-coins"></i>
            <span>Akshar Coins</span>
          </div> */}
          <div className="nav-item" onClick={() => navigate('/recruiter-my-profile')}>
            <i className="fas fa-user-circle"></i>
            <span>My Profile</span>
          </div>
          <div className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`} onClick={() => setActiveSection('settings')}>
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </div>
        </div>

        {/* Profile Completion Card */}
        <div className="sidebar-card" style={{ margin: '20px', padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
            Profile Complete
          </div>
          <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.3)', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{ width: `${Math.max(0, Math.min(100, profileCompletion || 0))}%`, height: '100%', background: '#fff', borderRadius: '4px', transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ fontSize: '30px', fontWeight: 700, color: '#fff' }}>
            {Math.max(0, Math.min(100, profileCompletion || 0))}%
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">

        {/* Content Area */}
        <div className="content-area">
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <div className="page-section active" id="dashboard">
              {/* Stats Grid - Modern Design */}
              <div className="stats-grid">
                <StatCard
                  title="Active Postings"
                  value={stats.activeJobs}
                  trend={stats.newJobsThisWeek > 0 ? 20 : 0}
                  trendValue={stats.newJobsThisWeek.toString()}
                  trendLabel="new this week"
                  icon={faBriefcase}
                  iconColor="#3b82f6"
                />
                
                <StatCard
                  title="Total Applications"
                  value={stats.totalApplications}
                  trend={stats.applicationsIncrease || 0}
                  trendValue=""
                  trendLabel="from last month"
                  icon={faUsers}
                  iconColor="#10b981"
                />
                
                <StatCard
                  title="In Interview Stage"
                  value={stats.inInterview}
                  trend={stats.interviewsThisWeek > 0 ? 12 : 0}
                  trendValue={stats.interviewsThisWeek.toString()}
                  trendLabel="scheduled this week"
                  icon={faUserClock}
                  iconColor="#8b5cf6"
                />
                
                <StatCard
                  title="Offers Extended"
                  value={stats.offersExtended}
                  trend={stats.offersAccepted > 0 ? 18 : 0}
                  trendValue={stats.offersAccepted.toString()}
                  trendLabel="accepted"
                  icon={faHandshake}
                  iconColor="#f97316"
                />
              </div>

              <div className="dashboard-content">
                <div className="main-content-section">
                  <div className="card">
                    <div className="table-container">
                      <table style={{ width: '100%', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th>Candidate</th>
                            <th>Position</th>
                            <th>Applied</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {applications.length > 0 ? (
                            applications.slice(0, 5).map((application, index) => (
                              <tr key={application._id || index}>
                                <td>
                                  <div className="candidate-info">
                                    <div className="candidate-avatar">
                                      {(application.candidate_name || application.applicant_name) ? 
                                        (application.candidate_name || application.applicant_name).charAt(0).toUpperCase() : 'C'}
                                    </div>
                                    <div className="candidate-details">
                                      <div className="candidate-name">
                                        {application.candidate_name || application.applicant_name || 'Unknown Candidate'}
                                      </div>
                                      <div className="candidate-position">
                                        {application.company_name || 'Company not specified'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="position-cell">{application.job_title || 'N/A'}</td>
                                <td className="date-cell">
                                  {formatDate(application.applied_at || application.created_at)}
                                </td>
                                <td>
                                  <span className={`status-badge status-${application.status || 'pending'}`}>
                                    {application.status_display || application.status || 'Pending'}
                                  </span>
                                </td>
                                <td>
                                  <button 
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleViewCandidate(
                                      application.applicant_id || application.userId, 
                                      application.job_id
                                    )}
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" style={{ 
                                width: '100%',
                                textAlign: 'center', 
                                verticalAlign: 'middle', 
                                padding: '60px 20px'
                              }}>
                                <div style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center', 
                                  justifyContent: 'center', 
                                  gap: '12px',
                                  margin: '0 auto'
                                }}>
                                  <i className="fas fa-users" style={{ fontSize: '48px', color: '#cbd5e0' }}></i>
                                  <p style={{ margin: 0, fontSize: '16px', fontWeight: 500, color: '#718096' }}>No applications yet</p>
                                  <small style={{ color: '#a0aec0', fontSize: '14px' }}>Applications will appear here when candidates apply to your jobs</small>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Post Job Section */}
          {activeSection === 'jobs' && (
            <div className="page-section active" id="jobs" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <div className="card job-postings-card">
                <div 
                  className="card-header job-postings-header"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Search Bar */}
                  <div style={searchContainerStyle}>
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 15px',
                        fontSize: '13px',
                        border: '2px solid #FF8A65',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#333',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <i className="fas fa-search" style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#FF8A65',
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}></i>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Filter Dropdown */}
                    <select
                      value={jobFilter}
                      onChange={(e) => setJobFilter(e.target.value)}
                      style={{
                        padding: '8px 15px',
                        fontSize: '13px',
                        border: '2px solid #FF8A65',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#333',
                        cursor: 'pointer',
                        width: 'auto',
                        minWidth: '160px',
                        fontWeight: '500',
                        outline: 'none',
                        display: 'inline-block',
                        boxSizing: 'border-box'
                      }}
                      className="filter-dropdown-compact"
                    >
                      <option value="all">All Jobs ({jobs.length})</option>
                      <option value="active">Active ({jobs.filter(job => !job.status || job.status === 'active').length})</option>
                      <option value="draft">Drafts ({jobs.filter(job => job.status === 'draft').length})</option>
                      <option value="closed">Closed ({jobs.filter(job => job.status === 'closed').length})</option>
                    </select>

                    <button 
                      className="btn btn-primary"
                      onClick={handlePostJobClick}
                      style={{ 
                        ...actionButtonStyle, 
                        minWidth: '110px',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '11px' }}></i> Post Job
                    </button>
                  </div>
                </div>

                <div className="table-container" style={{ position: 'relative', overflow: 'visible', paddingBottom: '24px' }}>
                  <table style={{ width: '100%', tableLayout: 'fixed', overflow: 'visible' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '16%', whiteSpace: 'nowrap' }}>Job Title</th>
                        <th style={{ width: '14%', whiteSpace: 'nowrap' }}>Company</th>
                        <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Type</th>
                        <th style={{ width: '12%', whiteSpace: 'nowrap' }}>Applications</th>
                        <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Views</th>
                        <th style={{ width: '12%', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ width: '22%', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredJobs = jobs.filter(job => {
                          const statusMatch = jobFilter === 'all' || 
                            (jobFilter === 'active' && (!job.status || job.status === 'active')) ||
                            (job.status === jobFilter);
                          const searchMatch = !searchTerm || 
                            (job.job_title || job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (job.job_type || job.jobType || job.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (job.company_name || job.companyName || job.company || '').toLowerCase().includes(searchTerm.toLowerCase());
                          return statusMatch && searchMatch;
                        });
                        
                        return filteredJobs.length > 0 ? (
                          filteredJobs.map((job, index) => (
                          <tr key={index}>
                            <td>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <strong style={{ color: '#FF8A65', fontSize: '14px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {job.job_title || job.title || 'Untitled Job'}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {job.company_name || job.companyName || job.company || '—'}
                              </div>
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <span style={{ 
                                background: '#fff3e0', 
                                color: '#FF8A65', 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                fontSize: '12px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                {job.job_type || job.jobType || job.type || 'Full-time'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {Math.min(25, job.visibleApplications !== undefined 
                                ? job.visibleApplications
                                : (job.applicationCount || job.applicants?.length || 0))
                              }
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {job.views || 0}
                            </td>
                            <td>
                              <span 
                                className={`status-badge status-${(job.status || 'active').toLowerCase()}`}
                                style={{
                                  background: (job.status || 'active').toLowerCase() === 'active' ? '#e8f5e8' : '#fee2e2',
                                  color: (job.status || 'active').toLowerCase() === 'active' ? '#2e7d32' : '#991b1b',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  textTransform: 'uppercase'
                                }}
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await fetch(
                                      buildApiUrl(`/api/jobs/jobs/${job._id}/toggle-status`),
                                      {
                                        method: 'PUT',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      }
                                    );
                                    
                                    if (response.ok) {
                                      const data = await response.json();
                                      alert(`✅ Job is now ${data.status.toUpperCase()}`);
                                      fetchJobs();
                                    } else {
                                      alert('❌ Failed to update status');
                                    }
                                  } catch (error) {
                                    console.error('Error toggling status:', error);
                                    alert('❌ Error updating status');
                                  }
                                }}
                                title="Click to toggle between Active/Inactive"
                              >
                                {(job.status || 'Active').toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  onClick={(e) => {
                                    const menuId = job._id || job.id || index;
                                    handleToggleJobMenu(menuId, job, e);
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '6px 14px',
                                    borderRadius: '5px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    minWidth: '110px',
                                    fontWeight: '500',
                                    boxShadow: '0 4px 10px rgba(255, 138, 101, 0.25)'
                                  }}
                                >
                                  <i className="fas fa-ellipsis-v" style={{ fontSize: '12px' }}></i>
                                  Actions
                                  <i className="fas fa-chevron-down" style={{ fontSize: '10px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                        ) : (
                          <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                              <i className="fas fa-briefcase" style={{ fontSize: '48px', color: '#FF8A65', marginBottom: '16px' }}></i>
                              <h3>{jobs.length === 0 ? 'No jobs posted yet' : `No ${jobFilter} jobs`}</h3>
                              <p>{jobs.length === 0 ? 'Start by posting your first job to see it here!' : `You don't have any ${jobFilter} jobs at the moment.`}</p>
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Post Internship Section */}
          {activeSection === 'internships' && (
            <div className="page-section active" id="internships" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <div className="card job-postings-card">
                <div 
                  className="card-header job-postings-header"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Search Bar */}
                  <div style={searchContainerStyle}>
                    <input
                      type="text"
                      placeholder="Search internships..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 15px',
                        fontSize: '13px',
                        border: '2px solid #FF8A65',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#333',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <i className="fas fa-search" style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#FF8A65',
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}></i>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Filter Dropdown */}
                    <select
                      value={internshipFilter}
                      onChange={(e) => setInternshipFilter(e.target.value)}
                      style={{
                        padding: '8px 15px',
                        fontSize: '13px',
                        border: '2px solid #FF8A65',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#333',
                        cursor: 'pointer',
                        width: 'auto',
                        minWidth: '160px',
                        fontWeight: '500',
                        outline: 'none',
                        display: 'inline-block',
                        boxSizing: 'border-box'
                      }}
                      className="filter-dropdown-compact"
                    >
                      <option value="all">All Internships ({internships.length})</option>
                      <option value="active">Active ({internships.filter(internship => !internship.status || internship.status === 'active').length})</option>
                      <option value="draft">Drafts ({internships.filter(internship => internship.status === 'draft').length})</option>
                      <option value="closed">Closed ({internships.filter(internship => internship.status === 'closed').length})</option>
                    </select>

                    <button 
                      className="btn btn-primary"
                      onClick={handlePostInternshipClick}
                      style={{ 
                        ...actionButtonStyle, 
                        minWidth: '110px',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '11px' }}></i> Post Internship
                    </button>
                  </div>
                </div>

                <div className="table-container">
                  <table style={{ width: '100%', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '16%', whiteSpace: 'nowrap' }}>Internship Title</th>
                        <th style={{ width: '14%', whiteSpace: 'nowrap' }}>Company</th>
                        <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Duration</th>
                        <th style={{ width: '12%', whiteSpace: 'nowrap' }}>Applications</th>
                        <th style={{ width: '8%', whiteSpace: 'nowrap' }}>Views</th>
                        <th style={{ width: '12%', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ width: '22%', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredInternships = internships.filter(internship => {
                          const statusMatch = internshipFilter === 'all' || 
                            (internshipFilter === 'active' && (!internship.status || internship.status === 'active')) ||
                            (internship.status === internshipFilter);
                          const searchMatch = !searchTerm || 
                            (internship.jobTitle || internship.job_title || internship.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (internship.jobType || internship.job_type || internship.duration || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (internship.company || internship.companyName || internship.company_name || '').toLowerCase().includes(searchTerm.toLowerCase());
                          return statusMatch && searchMatch;
                        });
                        
                        return filteredInternships.length > 0 ? (
                          filteredInternships.map((internship, index) => (
                          <tr key={index}>
                            <td>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <strong style={{ color: '#FF8A65', fontSize: '14px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {internship.jobTitle || internship.job_title || internship.title || 'Untitled Internship'}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {internship.company || internship.companyName || internship.company_name || '—'}
                              </div>
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <span style={{ 
                                background: '#fff3e0', 
                                color: '#FF8A65', 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                fontSize: '12px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}>
                                {internship.duration || internship.jobType || internship.job_type || '3-6 months'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {Math.min(25, internship.visibleApplications !== undefined 
                                ? internship.visibleApplications
                                : (internship.applicationCount || internship.applicants?.length || 0))
                              }
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {internship.views || 0}
                            </td>
                            <td>
                              <span 
                                className={`status-badge status-${(internship.status || 'active').toLowerCase()}`}
                                style={{
                                  background: (internship.status || 'active').toLowerCase() === 'active' ? '#e8f5e8' : '#fee2e2',
                                  color: (internship.status || 'active').toLowerCase() === 'active' ? '#2e7d32' : '#991b1b',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  textTransform: 'uppercase'
                                }}
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await fetch(
                                      buildApiUrl(`/api/recruiters/internships/${internship._id}/toggle-status`),
                                      {
                                        method: 'PUT',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      }
                                    );
                                    
                                    if (response.ok) {
                                      const data = await response.json();
                                      alert(`✅ Internship is now ${data.status.toUpperCase()}`);
                                      fetchInternships();
                                    } else {
                                      alert('❌ Failed to update status');
                                    }
                                  } catch (error) {
                                    console.error('Error toggling status:', error);
                                    alert('❌ Error updating status');
                                  }
                                }}
                                title="Click to toggle between Active/Inactive"
                              >
                                {(internship.status || 'Active').toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  onClick={(e) => {
                                    const menuId = internship._id || internship.id || index;
                                    handleToggleInternshipMenu(menuId, internship, e);
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '6px 14px',
                                    borderRadius: '5px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    minWidth: '110px',
                                    fontWeight: '500',
                                    boxShadow: '0 4px 10px rgba(255, 138, 101, 0.25)'
                                  }}
                                >
                                  <i className="fas fa-ellipsis-v" style={{ fontSize: '12px' }}></i>
                                  Actions
                                  <i className="fas fa-chevron-down" style={{ fontSize: '10px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <i className="fas fa-graduation-cap" style={{ fontSize: '48px', color: '#FF8A65', marginBottom: '16px' }}></i>
                            <h3>{internships.length === 0 ? 'No internships posted yet' : `No ${internshipFilter} internships`}</h3>
                            <p>{internships.length === 0 ? 'Start by posting your first internship to see it here!' : `You don't have any ${internshipFilter} internships at the moment.`}</p>
                          </td>
                        </tr>
                      );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Candidate Tracker Section */}
          {activeSection === 'candidate-tracker' && (
            <div className="page-section active" id="candidate-tracker">
              <RecruiterCandidateTracker onViewCandidate={handleViewCandidate} viewUserId={viewUserId} />
            </div>
          )}

          {/* Other sections */}
          {activeSection === 'messages' && (
            <div className="page-section active" style={{ marginLeft: '-20px', marginRight: '-20px', marginTop: '-20px' }}>
              <ProfessionalMessaging />
            </div>
          )}

          {activeSection === 'calendar' && (
            <div className="page-section active" style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '-1rem', paddingTop: '0' }}>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: '2 1 640px', minWidth: '640px', paddingRight: '20px' }}>
                  <InterviewCalendar
                    variant="employer"
                    onUpcomingChange={setUpcomingInterviews}
                    viewUserId={viewUserId}
                  />
                </div>
                <div style={{ flex: '1 1 360px', minWidth: '320px', marginTop: '160px' }}>
                  <UpcomingInterviewsPanel interviews={upcomingInterviews} />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'analytics' && (
            <div className="page-section active">
              <AnalyticsDashboard />
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="page-section active">
              <RecruiterSettings user={user} onUpdateUser={handleUserUpdate} />
            </div>
          )}
        </div>
      </div>

      {/* Job View/Edit Modal */}
      {showJobModal && selectedJob && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="modal-content" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#FF8A65', margin: 0 }}>
                {isEditing ? 'Edit Job' : 'Job Details'}
              </h2>
              <button 
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            
            {isEditing ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      {editJobData.title !== undefined ? 'Internship Title' : 'Job Title'}
                    </label>
                    <input
                      type="text"
                      value={editJobData.job_title || editJobData.title || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData, 
                        ...(editJobData.title !== undefined ? {title: e.target.value} : {job_title: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Company</label>
                    <input
                      type="text"
                      value={editJobData.company_name || editJobData.company || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData,
                        ...(editJobData.company !== undefined ? {company: e.target.value} : {company_name: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Location</label>
                    <input
                      type="text"
                      value={editJobData.location || ''}
                      onChange={(e) => setEditJobData({...editJobData, location: e.target.value})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      {editJobData.type !== undefined ? 'Type' : 'Job Type'}
                    </label>
                    <input
                      type="text"
                      value={editJobData.job_type || editJobData.type || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData,
                        ...(editJobData.type !== undefined ? {type: e.target.value} : {job_type: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      {editJobData.stipend !== undefined ? 'Stipend' : 'Salary Range'}
                    </label>
                    <input
                      type="text"
                      value={editJobData.salary_range || editJobData.stipend || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData,
                        ...(editJobData.stipend !== undefined ? {stipend: e.target.value} : {salary_range: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      {editJobData.domain !== undefined ? 'Domain' : 'Industry'}
                    </label>
                    <input
                      type="text"
                      value={editJobData.industry || editJobData.domain || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData,
                        ...(editJobData.domain !== undefined ? {domain: e.target.value} : {industry: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Description</label>
                  <textarea
                    value={editJobData.description || ''}
                    onChange={(e) => setEditJobData({...editJobData, description: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
                  />
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Required Skills</label>
                  <input
                    type="text"
                    value={editJobData.required_skills || editJobData.requiredSkills || ''}
                    onChange={(e) => setEditJobData({
                      ...editJobData,
                      ...(editJobData.requiredSkills !== undefined ? {requiredSkills: e.target.value} : {required_skills: e.target.value})
                    })}
                    placeholder="e.g., JavaScript, React, Node.js"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>

                <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <input
                      id="rec-edit-show-company-name"
                      type="checkbox"
                      checked={editJobData.showCompanyName !== false}
                      onChange={(e) => setEditJobData({...editJobData, showCompanyName: e.target.checked})}
                      style={{ marginTop: '4px' }}
                    />
                    <div>
                      <label htmlFor="rec-edit-show-company-name" style={{ fontWeight: '500', cursor: 'pointer' }}>Show company name on public listing</label>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Display your company name to candidates. Uncheck for confidential posting.</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <input
                      id="rec-edit-show-hr-name"
                      type="checkbox"
                      checked={editJobData.showHrName !== false}
                      onChange={(e) => setEditJobData({...editJobData, showHrName: e.target.checked})}
                      style={{ marginTop: '4px' }}
                    />
                    <div>
                      <label htmlFor="rec-edit-show-hr-name" style={{ fontWeight: '500', cursor: 'pointer' }}>Show employer / HR name to candidates</label>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Let applicants see the hiring manager name. Uncheck to keep private.</p>
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCloseModal}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #ddd',
                      background: 'white',
                      color: '#666',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveJob}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <h3 style={{ color: '#FF8A65', marginBottom: '10px' }}>Job Information</h3>
                    <p><strong>Title:</strong> {selectedJob.job_title || selectedJob.title || 'N/A'}</p>
                    <p><strong>Company:</strong> {selectedJob.company_name || selectedJob.company || 'N/A'}</p>
                    <p><strong>Location:</strong> {selectedJob.location || 'N/A'}</p>
                    <p><strong>Type:</strong> {selectedJob.job_type || selectedJob.type || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 style={{ color: '#FF8A65', marginBottom: '10px' }}>Job Stats</h3>
                    <p><strong>Applications:</strong> {
                      selectedJob.visibleApplications !== undefined 
                        ? selectedJob.visibleApplications
                        : (selectedJob.applicants ? selectedJob.applicants.length : 0)
                    }</p>
                    <p><strong>Views:</strong> {selectedJob.views || 0}</p>
                    <p><strong>Created:</strong> {selectedJob.created_at ? new Date(selectedJob.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCloseModal}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #ddd',
                      background: 'white',
                      color: '#666',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleEditJob(selectedJob)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit Job
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    {(openInternshipMenu && activeInternshipForMenu && internshipMenuPosition) && (
      <div
        className="employer-action-menu-portal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: internshipMenuPosition.top,
          left: internshipMenuPosition.left,
          width: internshipMenuPosition.width,
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.15)',
          padding: '6px 0',
          zIndex: 5000
        }}
      >
        <button
          onClick={() => handleInternshipMenuAction('view')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-eye" style={{ color: '#FF8A65' }}></i>
          View
        </button>
        <button
          onClick={() => handleInternshipMenuAction('edit')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-edit" style={{ color: '#14b8a6' }}></i>
          Edit
        </button>
        <button
          onClick={() => handleInternshipMenuAction('delete')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#dc2626',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-trash-alt" style={{ color: '#dc2626' }}></i>
          Delete
        </button>
      </div>
    )}
    {(openJobMenu && activeJobForMenu && jobMenuPosition) && (
      <div
        className="employer-action-menu-portal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: jobMenuPosition.top,
          left: jobMenuPosition.left,
          width: jobMenuPosition.width,
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.15)',
          padding: '6px 0',
          zIndex: 5000
        }}
      >
        <button
          onClick={() => handleJobMenuAction('view')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-eye" style={{ color: '#FF8A65' }}></i>
          View
        </button>
        <button
          onClick={() => handleJobMenuAction('edit')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-edit" style={{ color: '#14b8a6' }}></i>
          Edit
        </button>
        <button
          onClick={() => handleJobMenuAction('delete')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#dc2626',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-trash-alt" style={{ color: '#dc2626' }}></i>
          Delete
        </button>
      </div>
    )}
    </>
  );
};

export default RecruiterDashboard;